import React, { useState, useEffect } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../contexts/AuthContext';
import { useI18n, getAcceptLanguageHeader } from '../i18n';

const ROLE_OPTIONS = [
  { label: 'Оператор',            value: 'operator' },
  { label: 'Админ',               value: 'admin' },
  { label: 'Контроллер',          value: 'controller' },
  { label: 'Менеджер розыгрышей', value: 'raffle_manager' },
  { label: 'Менеджер магазина',   value: 'store_manager' },
  { label: 'Статистик',           value: 'statistic' },
];

const ROLE_LABELS = ROLE_OPTIONS.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {});

const ROLE_COLORS = {
  admin:          { bg: '#ede9fe', color: '#5b21b6' },
  operator:       { bg: '#dbeafe', color: '#1e40af' },
  controller:     { bg: '#fef9c3', color: '#854d0e' },
  raffle_manager: { bg: '#fce7f3', color: '#9d174d' },
  store_manager:  { bg: '#ffedd5', color: '#9a3412' },
  statistic:      { bg: '#dcfce7', color: '#166534' },
};

const DEFAULT_ROLE = ROLE_OPTIONS[0].value;

const AVATAR_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#3b82f6', '#84cc16'];

function RoleBadge({ role }) {
  const cfg = ROLE_COLORS[role] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: '20px',
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {ROLE_LABELS[role] || role}
    </Box>
  );
}

function UserAvatar({ name, id, size = 36 }) {
  const initials = (name || '')
    .split(' ')
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
  const color = AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, fontWeight: 700, backgroundColor: color, flexShrink: 0 }}>
      {initials}
    </Avatar>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', role: DEFAULT_ROLE, login: '', password: '' });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/users', {
          method: 'GET',
          headers: {
            Authorization: user?.api_token ? `Bearer ${user.api_token}` : '',
            'Accept-Language': getAcceptLanguageHeader(),
          },
        });
        if (!res.ok) throw new Error(t('Ошибка загрузки пользователей'));
        const data = await res.json();
        setUsers(Array.isArray(data.users) ? data.users : []);
      } catch (e) {
        setError(e.message || t('Ошибка загрузки пользователей'));
      } finally {
        setLoading(false);
      }
    };
    if (user && user.role === 'admin') fetchUsers();
    // eslint-disable-next-line
  }, [user]);

  if (!user || user.role !== 'admin') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 4, p: 2, bgcolor: '#fee2e2', borderRadius: 2, color: '#991b1b', fontWeight: 600 }}>
        <CancelIcon />
        {t('Доступ запрещён. Только для администратора.')}
      </Box>
    );
  }

  const handleAdd = async () => {
    if (users.some(u => u.login === form.login.trim())) {
      setError('Логин должен быть уникальным');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: user.api_token ? `Bearer ${user.api_token}` : '',
        },
        body: JSON.stringify({
          role: form.role,
          login: form.login.trim(),
          name: form.name.trim(),
          password: form.password,
        }),
      });
      if (!res.ok) throw new Error(t('Ошибка создания пользователя'));
      const data = await res.json();
      setUsers(prev => [
        ...prev,
        {
          id: data.id || (prev.length ? Math.max(...prev.map(u => u.id)) + 1 : 1),
          name: form.name.trim(),
          role: form.role,
          login: form.login.trim(),
          password: form.password,
        },
      ]);
      setForm({ name: '', role: DEFAULT_ROLE, login: '', password: '' });
      setError('');
      setOpen(false);
      setSuccessMsg(t('Пользователь успешно создан'));
    } catch (e) {
      setError(e.message || t('Ошибка создания пользователя'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/users/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: user.api_token ? `Bearer ${user.api_token}` : '' },
      });
      if (!res.ok) throw new Error(t('Ошибка удаления пользователя'));
      setUsers(prev => prev.filter(u => u.id !== deleteId));
      setSuccessMsg(t('Пользователь удалён'));
    } catch (e) {
      setError(e.message || t('Ошибка удаления пользователя'));
    } finally {
      setLoading(false);
      setDeleteId(null);
      setConfirmOpen(false);
    }
  };

  const isFormValid = form.name.trim() && form.login.trim() && form.password.trim();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{t('Сотрудники')}</Typography>
          {users.length > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {users.length} {t('всего')}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => { setOpen(true); setError(''); }}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {t('Добавить пользователя')}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Сотрудник')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Роль')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Login</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} align="right">{t('Действия')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {t('Пользователи не найдены')}
                  </TableCell>
                </TableRow>
              ) : (
                users.map(u => (
                  <TableRow
                    key={u.id}
                    sx={{ '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s' }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <UserAvatar name={u.name} id={u.id} size={34} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><RoleBadge role={u.role} /></TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary', fontFamily: 'monospace' }}>{u.login}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('Удалить пользователя')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setDeleteId(u.id); setConfirmOpen(true); }}
                          disabled={loading}
                          sx={{
                            border: '1px solid',
                            borderColor: 'error.light',
                            borderRadius: 1.5,
                            opacity: 0.6,
                            '&:hover': { opacity: 1, bgcolor: '#fee2e2' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Диалог добавления */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{t('Добавить пользователя')}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              label={t('Имя')}
              type="text"
              fullWidth
              size="small"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t('Роль')}</InputLabel>
              <Select
                label={t('Роль')}
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                {ROLE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <RoleBadge role={option.value} />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Login"
              type="text"
              fullWidth
              size="small"
              value={form.login}
              onChange={e => setForm(f => ({ ...f, login: e.target.value }))}
              error={!!error}
              helperText={error}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label={t('Пароль')}
              type="password"
              fullWidth
              size="small"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('Отмена')}
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!isFormValid || loading}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : t('Добавить')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 360 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{t('Удалить пользователя?')}</DialogTitle>
        <DialogContent sx={{ pt: '4px !important' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('Вы уверены, что хотите удалить этого пользователя? Это действие необратимо.')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('Отмена')}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : t('Удалить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMsg('')} severity="success" sx={{ width: '100%' }}>
          {successMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error && !open}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
